import React, { useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { fetchProject } from '../store/slices/projectSlice'
import ProjectTabs from './ProjectTabs'
import { FolderIcon } from '@heroicons/react/24/outline'
import { getImageUrl } from '../utils/formatters'

interface ProjectLayoutProps {
  children: React.ReactNode
}

const ProjectLayout: React.FC<ProjectLayoutProps> = ({ children }) => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { currentProject, loading } = useAppSelector(state => state.project)

  // 財務管理底下的頁面隱藏專案標題區與分頁列，騰出空間給財務自己的 UI
  const hideProjectChrome = /\/project\/[^/]+\/finance(\/|$)/.test(location.pathname)

  useEffect(() => {
    if (projectKey) {
      dispatch(fetchProject(projectKey))
    }
  }, [dispatch, projectKey])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">專案不存在</h3>
        <p className="text-gray-500">請檢查專案代碼是否正確</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* 專案標題區域 */}
      {!hideProjectChrome && (
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg overflow-hidden bg-primary-100 flex items-center justify-center flex-shrink-0">
              {currentProject.icon ? (
                <img
                  src={getImageUrl(currentProject.icon) || currentProject.icon}
                  alt={`${currentProject.name} icon`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent && !parent.querySelector('svg')) {
                      const fallback = document.createElement('div');
                      fallback.innerHTML = '<svg class="h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-6.911Z" /></svg>';
                      parent.appendChild(fallback);
                    }
                  }}
                />
              ) : (
                <FolderIcon className="h-5 w-5 text-primary-600" />
              )}
            </div>
            <div>
              <span>{currentProject.name}</span>
              <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded ml-2">
                {currentProject.key}
              </span>
            </div>
          </h1>
          {currentProject.description && (
            <p className="text-gray-600 text-sm mt-1">
              {currentProject.description}
            </p>
          )}
        </div>
      </div>
      )}

      {/* 分頁導航 */}
      {!hideProjectChrome && <ProjectTabs />}

      {/* 主要內容區域 */}
      <div className="flex-1 bg-gray-50">
        {children}
      </div>
    </div>
  )
}

export default ProjectLayout